import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import useUser from '../hooks/useUser';

const ProductMultiBranch = () => {
    const toast = useRef(null);
    const navigate = useNavigate();

    const { loggedInUserId, selectedBranchId, userRole, userClient, logProcess } = useUser();

    const [clients, setClients] = useState([]);
    const [categories, setCategories] = useState([]);
    const [branches, setBranches] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [taxes, setTaxes] = useState([]);

    const [form, setForm] = useState({
        clientid: null,
        category_master_id: null,
        platformid: null,
        productName: '',
        hsnCode: '',
        sortingOrder: '',
        uom: null,
        discountPercent: '',
        token: '',
        tokengroup: '',
        salesItemType: null,
        productType: null,
        productImage: null,
        barcode: '',
        effectiveFrom: null,
        status: 'Active'
    });

    const [branchPrices, setBranchPrices] = useState([]);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation', {
                headers: {
                    'Client-Id': userClient || '',
                    'User-Role': userRole || ''
                }
            });

            const data = res.data || [];
            setClients(data);

            if (userRole && userRole !== 'GSPOS') {
                let clientToSelect = null;

                if (userClient) {
                    const found = data.find((c) => String(c.client_id) === String(userClient));
                    if (found) clientToSelect = found.client_id;
                }

                if (!clientToSelect && data.length > 0) {
                    clientToSelect = data[0].client_id;
                }

                if (clientToSelect) {
                    setForm((prev) => ({ ...prev, clientid: clientToSelect }));
                    await fetchBranches(clientToSelect);
                    await fetchCategories(clientToSelect, null);
                }
            } else {
                setForm((prev) => ({
                    ...prev,
                    clientid: null,
                    branchid: null,
                    prcat_cat_id: null,
                    category_master_id: null
                }));
                setBranches([]);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load clients'
            });
        }
    };

    const fetchCategories = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/get_asgcategories', {
                params: { clientid: clientId }
            });
            setCategories(res.data.map((c) => ({ label: c.category_name, value: c.category_master_id })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load categories' });
        }
    };

    const fetchBranches = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', {
                params: { clientid: clientId }
            });
            const branchData = res.data.map((b) => ({
                branch_id: b.branch_id,
                branch_name: b.branch_name,
                basicRate: '',
                taxPercent: '',
                taxAmount: '',
                totalPrice: ''
            }));
            setBranches(branchData);
            setBranchPrices(branchData);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        }
    };

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms(res.data.map((p) => ({ label: p.platform_name, value: p.platform_id })));
        } catch {}
    };

    const fetchUoms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/uom');
            setUoms(res.data.map((u) => ({ label: u.uom_name, value: u.uom_name })));
        } catch {}
    };

    const fetchTaxes = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/taxpercent');
            setTaxes(res.data.map((t) => ({ label: `${t.tax_percentage}%`, value: t.tax_percentage })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load taxes' });
        }
    };

    useEffect(() => {
        if (userRole !== undefined && userRole !== null) {
            fetchClients();
            fetchPlatforms();
            fetchUoms();
            fetchTaxes();
        }
    }, [userRole, userClient]);

    const handleChange = (key, value, option = null) => {
        let updated = { ...form, [key]: value };

        if (key === 'clientid') {
            fetchCategories(value);
            setCategories([]);
            setBranches([]);
            setBranchPrices([]);
        }

        if (key === 'category_master_id' && option) {
            updated.category_master_id = value;
            updated.category_name = option.label;
        }

        setForm(updated);
    };

    const handleBranchPriceChange = (branchId, field, value) => {
        const updated = branchPrices.map((b) => {
            if (b.branch_id === branchId) {
                let updatedBranch = { ...b, [field]: value };

                if (field === 'taxPercent' || field === 'totalPrice') {
                    const taxPercent = parseFloat(updatedBranch.taxPercent) || 0;
                    const totalPrice = parseFloat(updatedBranch.totalPrice) || 0;
                    if (totalPrice > 0) {
                        const basicRate = totalPrice / (1 + taxPercent / 100);
                        const taxAmount = totalPrice - basicRate;
                        updatedBranch.basicRate = basicRate.toFixed(2);
                        updatedBranch.taxAmount = taxAmount.toFixed(2);
                    }
                }
                return updatedBranch;
            }
            return b;
        });
        setBranchPrices(updated);
    };

    const validateForm = () => {
        const requiredFields = [
            { key: 'clientid', label: 'Client' },
            { key: 'category_master_id', label: 'Category' },
            { key: 'productName', label: 'Product Name' },
            { key: 'platformid', label: 'Platform' },
            { key: 'uom', label: 'UOM' },
            { key: 'salesItemType', label: 'Sales Item Type' },
            { key: 'productType', label: 'Product Type' },
            { key: 'effectiveFrom', label: 'Effective From' },
            { key: 'status', label: 'Status' }
        ];

        for (let field of requiredFields) {
            if (!form[field.key]) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `${field.label} is required`,
                    life: 3000
                });
                return false;
            }
        }

        // Validate branch prices
        for (let branch of branchPrices) {
            if (!branch.totalPrice || !branch.taxPercent) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Branch "${branch.branch_name}" must have Total Price and Tax %`,
                    life: 3000
                });
                return false;
            }
        }

        return true;
    };

    const confirmSave = () => {
        if (!validateForm()) return;

        confirmDialog({
            message: 'Save product for all selected branches?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: saveProduct
        });
    };

    const saveProduct = async () => {
        try {
            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (value !== null && value !== '') {
                    if (key === 'productImage' && value) {
                        formData.append('productImage', value);
                    } else if (key === 'effectiveFrom' && value instanceof Date) {
                        formData.append('effectiveFrom', value.toISOString().split('T')[0]);
                    } else {
                        formData.append(key, value);
                    }
                }
            });

            formData.append('branches', JSON.stringify(branchPrices));

            const res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/product_multi_branch', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

            if (res.data.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message });
                setTimeout(() => {
                    navigate('/productlist');
                }, 1200);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: res.data.message });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Save failed' });
        }
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <ConfirmDialog />
            <h2 className="text-xl font-bold mb-3">Add Product to Multiple Branches</h2>

            <div className="card">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12 md:col-3">
                        <label>
                            Client <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.clientid}
                            options={clients.map((c) => ({ label: c.client_name, value: c.client_id }))}
                            onChange={(e) => handleChange('clientid', e.value)}
                            placeholder="Select Client"
                            className="w-full flex-1"
                            filter
                            disabled={userRole !== 'GSPOS'}
                        />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>
                            Category <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.category_master_id}
                            options={categories}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => {
                                const selectedOption = categories.find((opt) => opt.value === e.value);
                                handleChange('category_master_id', e.value, selectedOption);
                            }}
                            placeholder="Select Category"
                            className="w-full flex-1"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>
                            Product Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText placeholder="Product Name" value={form.productName} onChange={(e) => handleChange('productName', e.target.value)} className="w-full flex-1" />
                    </div>
                    <div className="field col-12 md:col-2 flex align-items-end">
                        <Button label="Add Branches" onClick={() => fetchBranches(form.clientid)} className="w-full" />
                    </div>
                </div>
            </div>

            <div className="card">
                {branchPrices.length > 0 && (
                    <div className="mt-4">
                        <DataTable value={branchPrices} className="p-datatable-sm">
                            <Column field="branch_name" header="Branch" />
                            <Column
                                header={
                                    <span>
                                        Total Price <span style={{ color: 'red' }}>*</span>
                                    </span>
                                }
                                body={(row) => <InputText value={row.totalPrice} onChange={(e) => handleBranchPriceChange(row.branch_id, 'totalPrice', e.target.value)} />}
                            />
                            <Column
                                header={
                                    <span>
                                        Tax % <span style={{ color: 'red' }}>*</span>
                                    </span>
                                }
                                body={(row) => <Dropdown value={row.taxPercent} options={taxes} onChange={(e) => handleBranchPriceChange(row.branch_id, 'taxPercent', e.value)} placeholder="Select Tax %" className="w-full" filter />}
                            />
                            <Column field="basicRate" header="Basic Rate" />
                            <Column field="taxAmount" header="Tax Amount" />
                        </DataTable>
                    </div>
                )}

                <div className="grid formgrid p-fluid">
                    <div className="field col-12 md:col-6">
                        <label>
                            Platform <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.platformid} options={platforms} onChange={(e) => handleChange('platformid', e.value)} placeholder="Select Platform" filter />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>HSN Code</label>
                        <InputText value={form.hsnCode} onChange={(e) => handleChange('hsnCode', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Sorting Order</label>
                        <InputText value={form.sortingOrder} onChange={(e) => handleChange('sortingOrder', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            UOM <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.uom} options={uoms} onChange={(e) => handleChange('uom', e.value)} placeholder="Select UOM" filter />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Discount %</label>
                        <InputText value={form.discountPercent} onChange={(e) => handleChange('discountPercent', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Token</label>
                        <InputText value={form.token} onChange={(e) => handleChange('token', e.target.value)} />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>Token Group</label>
                        <InputText value={form.tokengroup} onChange={(e) => handleChange('tokengroup', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Sales Item Type <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.salesItemType}
                            options={[
                                { label: 'Free', value: 'free' },
                                { label: 'Paid', value: 'paid' }
                            ]}
                            onChange={(e) => handleChange('salesItemType', e.value)}
                            placeholder="Select Type"
                        />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Product Type <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.productType}
                            options={[
                                { label: 'Veg', value: 'veg' },
                                { label: 'Non-Veg', value: 'nonveg' },
                                { label: 'Egg', value: 'egg' }
                            ]}
                            onChange={(e) => handleChange('productType', e.value)}
                            placeholder="Select Product Type"
                        />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Status <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.status}
                            options={[
                                { label: 'Active', value: 'Active' },
                                { label: 'InActive', value: 'InActive' }
                            ]}
                            onChange={(e) => handleChange('status', e.value)}
                            placeholder="Select Status"
                        />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Product Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                handleChange('productImage', file);
                            }}
                        />

                        {/* Preview  */}
                        {form.productImage && (
                            <div className="mt-3">
                                <img src={URL.createObjectURL(form.productImage)} alt="Product Preview" style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ccc' }} />
                            </div>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Barcode</label>
                        <InputText value={form.barcode} onChange={(e) => handleChange('barcode', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Effective From <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Calendar value={form.effectiveFrom} onChange={(e) => handleChange('effectiveFrom', e.value)} showIcon />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button label="Save" icon="pi pi-check" onClick={confirmSave} />
                </div>
            </div>
        </div>
    );
};

export default ProductMultiBranch;
